/**
 * "Add to Home Screen": captures the browser's install prompt (Chrome, Edge, Samsung Internet on Android
 * and desktop) so a button can open it. iPhone and iPad have no prompt API; callers show instructions there.
 */
import { createStore, useStore } from './store';

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** True when the app was opened from its Home Screen icon. */
export function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export type Platform = 'ios' | 'android' | 'other';
export function platform(): Platform {
  const ua = navigator.userAgent;
  // iPadOS reports itself as a Mac; touch support tells them apart.
  if (/iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

const store = createStore<{ prompt: InstallPromptEvent | null; installed: boolean }>({ prompt: null, installed: isStandalone() });

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault(); // keep it for our own button instead of the browser's mini-infobar
  store.set({ prompt: e as InstallPromptEvent });
});
window.addEventListener('appinstalled', () => store.set({ prompt: null, installed: true }));

/** Opens the browser's install dialog. 'unavailable' means show manual instructions instead. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const p = store.get().prompt;
  if (!p) return 'unavailable';
  await p.prompt();
  const { outcome } = await p.userChoice;
  store.set({ prompt: null, installed: outcome === 'accepted' });
  return outcome;
}

export const useInstallState = () => useStore(store, (s) => s);
